import React, { useEffect, useState } from 'react';
import { css } from 'emotion';
import { LoadingIndicator } from '@spotify-internal/encore-web';
import { Modal } from '@spotify-internal/anchor-ds';
import { useQueryClient } from 'react-query';
import { connect } from 'react-redux';
import { useCurrentUserCtx } from 'contexts/CurrentUser';
import { useFeatureFlagsCtx } from 'contexts/FeatureFlags';
import { AnchorAPI } from 'modules/AnchorAPI';
import { getIsEPEnabled } from 'modules/FeatureFlags';
import { Metadata } from 'types/Metadata';
import { useFetchDistributionData } from 'client/hooks/distribution/useFetchDistributionData';
import { useFetchAndConstructPodcastObject } from 'client/hooks/podcast/useFetchAndConstructPodcastObject';
import { useFetchPodcastCategories } from 'client/hooks/podcast/useFetchPodcastCategories';
import { FETCH_PODCAST_OBJECT_QUERY } from 'client/hooks/distribution/useOptOutOfPodcastDistribution';
import {
  FETCH_PODCAST_STATUS_QUERY,
  useFetchPodcastStatus,
} from 'client/hooks/podcast/useFetchPodcastStatus';
import { mapPodcastCategories } from 'screens/SettingsScreen/components/SettingsForm/SettingsForm';
import { CoverArtModalScreenContainer } from '../CoverArtModalScreen';
import { duckOperations as coverArtModalScreenDuckOperations } from '../CoverArtModalScreen/duck';
import { ConfirmationModal } from './components/BinaryDistributionPrompt/ConfirmationModal';
import { MetadataForm, FormData } from './components/MetadataForm';
import { events } from './events';
import { BinaryDistributionPrompt } from './components/BinaryDistributionPrompt';
import { ERROR_MESSAGES } from '../SettingsScreen/components/SettingsForm/constants';

type Props = {
  onHideModal: () => void;
  setFinishedDistributionFlow?: (isFinished: boolean) => void;
  onSubmitFormFinish?: (
    updatedMetadata: Metadata,
    podcastImageFull?: string
  ) => void;
  onFinishSubmitCoverArt?: (params: {
    image400: string;
    image: string;
  }) => void;
  resetCoverArtModalState: () => void;
  setupCoverArtModalStore: () => void;
  onMTAcceptSuccess?: () => void;
  onSpotifyDistributionAcceptSuccess?: () => void;
  onRSSAcceptClick?: () => void;
  containsMusicSegments?: boolean;
  isMilestone?: boolean;
};

function DistributionModal({
  onHideModal,
  setFinishedDistributionFlow,
  onSubmitFormFinish,
  onFinishSubmitCoverArt,
  resetCoverArtModalState,
  setupCoverArtModalStore,
  onMTAcceptSuccess,
  onSpotifyDistributionAcceptSuccess,
  onRSSAcceptClick,
  containsMusicSegments,
  isMilestone,
}: Props) {
  const {
    state: { featureFlags },
  } = useFeatureFlagsCtx();
  const {
    state: { webStationId, userId },
  } = useCurrentUserCtx();
  const queryClient = useQueryClient();

  const [scene, setScene] = useState<
    'form' | 'publishing' | 'confirmation' | 'coverArt'
  >('form');

  // data requests
  const {
    podcast,
    handleSetPodcast,
    isLoadingPodcast,
  } = useFetchAndConstructPodcastObject();

  const { distributionData } = useFetchDistributionData();

  const { podcastCategoriesData } = useFetchPodcastCategories();

  const { podcastStatusData } = useFetchPodcastStatus();

  const podcastCategoryOptions = mapPodcastCategories(
    podcastCategoriesData?.podcastCategoryOptions || []
  );
  const isEPEnabled = getIsEPEnabled(featureFlags);
  const publishedEpisodes =
    podcast?.podcastEpisodes?.filter(
      episode => episode.isPublished || episode.publishOn !== null
    ) || [];
  const hasOnlyPublishedMTEpisodes =
    podcast?.podcastEpisodes &&
    publishedEpisodes.length > 0 &&
    publishedEpisodes.every(episode => episode.isMT);
  const isOptedIntoSpotifyDistribution =
    podcastStatusData?.podcastStatus === 'optedinspotify' ||
    podcastStatusData?.podcastStatus === 'active';
  const isRssFeedEnabled = !!distributionData?.isRssFeedEnabled;
  const isSpotifyExclusive = containsMusicSegments || isEPEnabled;
  const canEnableRss = !!distributionData?.canEnableRss;

  const stationType = isSpotifyExclusive ? 'show' : 'podcast';
  const {
    podcastName,
    language,
    podcastDescription,
    itunesCategory,
    podcastImageFull,
  } = podcast?.metadata || {};

  useEffect(() => {
    // since cover art modal is still tapped into redux we have to
    // reset the state even though this component is managing the cover
    // art modal's `isShowing` piece of state
    // in a perfect world with infinite time to refactor, the cover art
    // state wouldn't be globally exposed and we wouldn't have to reset its
    // state when it's no longer on the dom
    resetCoverArtModalState();
    events.viewPodcastSetupModal();

    return () => {
      // reset the state of the cover art modal when we're done
      resetCoverArtModalState();
    };
  }, [resetCoverArtModalState]);

  useEffect(() => {
    // if the user has already filled out podcast info and
    // selected an image, go to distribution prompt
    // unless it's the milestone step
    const hasMetadata = !!(
      podcastName &&
      language &&
      podcastDescription &&
      itunesCategory &&
      podcastImageFull
    );
    if (hasMetadata && !isMilestone) {
      setScene('publishing');
    }
  }, [
    podcastName,
    language,
    podcastDescription,
    itunesCategory,
    podcastImageFull,
    isMilestone,
  ]);

  const onSuccess = () => {
    queryClient.invalidateQueries(FETCH_PODCAST_STATUS_QUERY);
  };

  // podcast form callbacks
  const onSubmitSuccess = (data: any, imageFull?: string) => {
    const hasPodcastCoverArt = !!imageFull;
    if (!hasPodcastCoverArt) {
      // show cover art modal then dispatch action to set up store
      setScene('coverArt');
      setupCoverArtModalStore();
    } else if (!isMilestone) {
      setScene('publishing');
    }
    if (onSubmitFormFinish) onSubmitFormFinish(data, imageFull);
  };

  const onSubmitForm = async (
    updatedMetadata: FormData,
    imageFull?: string
  ) => {
    const {
      podcastName: name,
      language: podcastLanguage,
      podcastDescription: description,
      itunesCategory: podcastCategory,
    } = updatedMetadata;
    const resp = await AnchorAPI.updatePodcastMetadata(
      {
        userId: userId!,
        webStationId: webStationId!,
        metadata: {
          podcastName: name,
          podcastDescription: description,
          podcastLanguage, // this api is expecting 'podcastLanguage' and 'podcastCategory' in the request body
          podcastCategory,
        },
      },
      true
    );

    if (
      typeof resp !== 'number' &&
      resp.errors?.includes(ERROR_MESSAGES.PODCAST_NAME_NOT_ALLOWED)
    ) {
      throw new Error(ERROR_MESSAGES.PODCAST_NAME_NOT_ALLOWED);
    }
    onSubmitSuccess(updatedMetadata, imageFull);
    queryClient.invalidateQueries(FETCH_PODCAST_OBJECT_QUERY);
  };

  // cover art modal callbacks
  const onWillCloseCoverArtModal = () => {
    // close all the modals!
    resetCoverArtModalState();
    onHideModal();
  };

  const onDidPressNavigateFromChooseScene = () => {
    setScene('form');
    resetCoverArtModalState();
  };

  const onCoverArtFinish = ({
    image400,
    image,
  }: {
    image: string;
    image400: string;
  }) => {
    resetCoverArtModalState();
    if (isMilestone) {
      onHideModal();
    } else {
      setScene('publishing');
    }
    handleSetPodcast();
    if (onFinishSubmitCoverArt) onFinishSubmitCoverArt({ image400, image });
  };

  const Spinner = () => (
    <div
      className={css`
        display: flex;
        align-items: center;
        justify-content: center;
        height: 400px;
      `}
    >
      <LoadingIndicator
        indicatorSize={LoadingIndicator.sm}
        data-testid="loadingSpinner"
      />
    </div>
  );

  switch (scene) {
    case 'form':
      return (
        <Modal
          isShowing
          isShowingCloseButton
          onClose={onHideModal}
          aria-labelledby="pre-distribution-modal-title"
        >
          {podcastCategoryOptions && !isLoadingPodcast ? (
            [
              <Modal.Title id="pre-distribution-modal-title">
                Set up your {stationType}
              </Modal.Title>,
              <Modal.Subtitle>
                {isSpotifyExclusive
                  ? 'Before we can distribute your show to Spotify, you just need to fill in a few things.'
                  : 'Before we can distribute your podcast to additional listening platforms, you just need to fill in a few things.'}
                <span
                  className={css`
                    display: block;
                  `}
                >
                  (You can always change these later.)
                </span>
              </Modal.Subtitle>,
              <Modal.Body
                className={css`
                  width: 100%;
                `}
              >
                <MetadataForm
                  stationType={stationType}
                  podcastCategoryOptions={podcastCategoryOptions}
                  defaultValues={{
                    podcastName: podcastName || '',
                    podcastDescription: podcastDescription || '',
                    itunesCategory: itunesCategory || '',
                    language: language || '',
                  }}
                  onSubmit={(data: FormData) =>
                    onSubmitForm(data, podcastImageFull)
                  }
                />
              </Modal.Body>,
            ]
          ) : (
            <Spinner />
          )}
        </Modal>
      );
    case 'coverArt':
      return (
        <CoverArtModalScreenContainer
          onFinishSubmitCoverArt={onCoverArtFinish}
          onWillClose={onWillCloseCoverArtModal}
          onDidPressNavigateFromChooseScene={onDidPressNavigateFromChooseScene}
        />
      );
    case 'publishing':
      return (
        <BinaryDistributionPrompt
          isSpotifyExclusive={isSpotifyExclusive}
          isOptedIntoSpotifyDistribution={isOptedIntoSpotifyDistribution}
          hasOnlyPublishedMTEpisodes={hasOnlyPublishedMTEpisodes}
          isRssFeedEnabled={isRssFeedEnabled}
          podcastImageFull={podcastImageFull}
          stationId={webStationId}
          onClickClose={onHideModal}
          canEnableRss={canEnableRss}
          onMTAcceptSuccess={() => {
            if (onMTAcceptSuccess) onMTAcceptSuccess();
            onSuccess();
          }}
          onSpotifyDistributionAcceptSuccess={() => {
            onSuccess();
            setScene('confirmation');
          }}
          onRSSAcceptClick={() => {
            if (onRSSAcceptClick) onRSSAcceptClick();
            onSuccess();
          }}
        />
      );
    case 'confirmation':
      return (
        <ConfirmationModal
          onClickClose={onHideModal}
          onClickContinue={() => {
            if (onSpotifyDistributionAcceptSuccess)
              onSpotifyDistributionAcceptSuccess();
            if (setFinishedDistributionFlow) setFinishedDistributionFlow(true);
            onSuccess();
            onHideModal();
          }}
        />
      );
    default:
      return null;
  }
}

// If we ever refactor the cover art modal and remove its reliance on redux,
// this can go and we'd manage it's visibility state in the DistributionModal component
const mapDispatchToProps = (dispatch: any) => {
  return {
    resetCoverArtModalState: () => {
      dispatch(coverArtModalScreenDuckOperations.closeAndResetState());
    },
    setupCoverArtModalStore: () => {
      dispatch(coverArtModalScreenDuckOperations.openAndSetupStore());
    },
  };
};

const PreDistributionModal = connect(
  null,
  mapDispatchToProps
)(DistributionModal);

export { PreDistributionModal };
